import * as React from 'react';

import { cn } from '../../lib/cn';

/**
 * Blocco segnaposto del *corpo* di una card in caricamento: va a fuoco e
 * sfuma in loop (`skeleton-blur-flash`, definita in `legacy-utilities.css`)
 * invece del solo `animate-pulse` piatto dell'atom `Skeleton` — su un
 * rettangolo a tinta unita il pulse da solo passa quasi inosservato, qui
 * "lampeggiano" sia l'opacità sia la sfocatura.
 *
 * Il fondo è `var(--color-border)`, non `bg-muted`: è lo stesso colore che
 * `KpiBig`/`InlineProgressRow`/`DistributionBar` usano già per le barre
 * "vuote" sopra una card, quindi garantito visibile sia in chiaro sia in
 * scuro — `--muted` invece è quasi identico al fondo della card in chiaro
 * (card bianca, `--muted` un grigio-azzurro pallido) e lì il blocco spariva.
 *
 * Il titolo della card resta nitido — vedi {@link PanelCardSkeleton} —
 * perché quello lo sappiamo già: a sfocare/lampeggiare è solo il valore che
 * non c'è ancora.
 *
 * Esportato (non solo uso interno): serve anche alle pagine che ricostruiscono
 * a mano una forma di card non standard (es. la `.pcard` degli impianti in
 * Monitoraggio), dove gli skeleton di questo file non bastano.
 */
const BodySkeleton = ({ className, style, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('skeleton-blur-flash rounded-md', className)}
		style={{ background: 'var(--color-border)', ...style }}
		{...props}
	/>
);
BodySkeleton.displayName = 'BodySkeleton';

const INLINE_CONTROL_WIDTH = {
	sm: 'w-32',
	md: 'w-48',
	lg: 'w-64',
} as const;

export interface InlineControlSkeletonProps {
	/** Larghezza del controllo. Default: `md`. */
	width?: keyof typeof INLINE_CONTROL_WIDTH;
}

/**
 * Segnaposto di un controllo isolato fuori da una card (un `<select>`, un
 * bottone) — es. il selettore CER sopra le due `ListCard` di Configurazioni.
 * Niente `className` da passare: la forma di {@link BodySkeleton} qui non
 * basta perché chi la usa sta fuori da `packages/ui` e non può decidere le
 * dimensioni a mano (vietato dal confine di presentazione), quindi la
 * larghezza è una scelta tipizzata invece che una classe libera.
 */
const InlineControlSkeleton = ({ width = 'md' }: InlineControlSkeletonProps) => (
	<BodySkeleton className={cn('h-9 rounded-lg', INLINE_CONTROL_WIDTH[width])} />
);
InlineControlSkeleton.displayName = 'InlineControlSkeleton';

export interface PanelCardSkeletonProps {
	/**
	 * Titolo reale della card (es. "Energia Prodotta", "Top Soci"): resta
	 * leggibile durante il caricamento, non è un dato che deve ancora arrivare.
	 */
	title: React.ReactNode;
	/** Mostra un badge segnaposto sfocato accanto al titolo (es. il pallino LIVE/ND di una card). */
	badge?: boolean;
	/** La card prende tutta l'altezza del contenitore, come {@link PanelCard} con `fill`. */
	fill?: boolean;
	/** Stacco verso la card successiva, come {@link PanelCard} con `spaced`: per le card fuori da una `CardGrid`. */
	spaced?: boolean;
	/** Titolo e contenuto centrati, come {@link PanelCard} con `centered` (le card-contatore a numero singolo). */
	centered?: boolean;
	/** Corpo della card: componi con {@link KpiSkeleton}, {@link ChartSkeleton}, {@link ListSkeleton}, {@link RingSkeleton}. */
	children: React.ReactNode;
}

/**
 * Guscio di {@link PanelCard} in stato di caricamento: stessa cornice/padding
 * della card reale (classe `.card`/`.card-head`). Il titolo è quello vero —
 * l'utente sa già di cosa parla la card, solo il valore manca — mentre il
 * corpo è sfocato e pulsante finché il dato non arriva. Le pagine compongono
 * il corpo con gli altri skeleton di questo file, così la forma replica quella
 * della card che sta per arrivare invece di un rettangolo generico.
 */
const PanelCardSkeleton = ({
	title,
	badge,
	fill,
	spaced,
	centered,
	children,
}: PanelCardSkeletonProps) => (
	<div
		className={cn(
			'card',
			fill && 'flex h-full flex-col',
			spaced && 'mb-[22px]',
			centered && 'text-center [&>.card-head]:justify-center',
		)}
		// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), non un `<output>` isolato: il pattern role+aria-label è lo stesso di DistributionBar.
		role="status"
		aria-label="Caricamento…"
	>
		<div className="card-head">
			<span className="card-title">{title}</span>
			{badge && <BodySkeleton className="h-5 w-14 rounded-full" />}
		</div>
		{children}
	</div>
);
PanelCardSkeleton.displayName = 'PanelCardSkeleton';

/** Corpo skeleton di un {@link KpiBig}: numero centrale, riga unità, barra, riga di trend. */
const KpiSkeleton = () => (
	<div className="flex flex-col items-center gap-3 py-1">
		<BodySkeleton className="h-9 w-28" />
		<BodySkeleton className="h-3 w-36" />
		<BodySkeleton className="h-1.5 w-full rounded-[3px]" />
		<BodySkeleton className="h-3 w-40" />
	</div>
);
KpiSkeleton.displayName = 'KpiSkeleton';

export interface ChartSkeletonProps {
	/** Altezza del riquadro grafico, in px. Default: 280. */
	height?: number;
}

/** Corpo skeleton di un grafico: riga di nota sopra, riquadro pieno sotto. */
const ChartSkeleton = ({ height = 280 }: ChartSkeletonProps) => (
	<div>
		<BodySkeleton className="mb-2.5 h-3 w-56" />
		<BodySkeleton className="w-full" style={{ height }} />
	</div>
);
ChartSkeleton.displayName = 'ChartSkeleton';

export interface ListRowSkeletonProps {
	/** Mostra un avatar circolare a sinistra della riga (es. iniziali socio). Default: true. */
	avatar?: boolean;
	/** Mostra un blocco valore a destra della riga (es. kWh/importo/badge conteggio). Default: true. */
	trailing?: boolean;
}

/** Riga skeleton di un elenco (socio, coda di lavorazione, report, …): avatar + due righe di testo + valore. */
const ListRowSkeleton = ({ avatar = true, trailing = true }: ListRowSkeletonProps) => (
	<div className="flex items-center gap-2.5 py-1.5">
		{avatar && <BodySkeleton className="h-8 w-8 shrink-0 rounded-full" />}
		<div className="min-w-0 flex-1 space-y-1.5">
			<BodySkeleton className="h-3 w-2/3" />
			<BodySkeleton className="h-2.5 w-1/3" />
		</div>
		{trailing && <BodySkeleton className="h-3 w-14 shrink-0" />}
	</div>
);
ListRowSkeleton.displayName = 'ListRowSkeleton';

export interface ListSkeletonProps {
	/** Numero di righe placeholder. Default: 5. */
	rows?: number;
	/** Passato a ogni {@link ListRowSkeleton}. */
	avatar?: boolean;
	/** Passato a ogni {@link ListRowSkeleton}. */
	trailing?: boolean;
}

/** Elenco di {@link ListRowSkeleton}, per il corpo di card-lista/tabella. */
const ListSkeleton = ({ rows = 5, avatar, trailing }: ListSkeletonProps) => (
	<div>
		{Array.from({ length: rows }).map((_, i) => (
			<ListRowSkeleton key={i} avatar={avatar} trailing={trailing} />
		))}
	</div>
);
ListSkeleton.displayName = 'ListSkeleton';

export interface KeyValueListSkeletonProps {
	/** Numero di righe etichetta/valore. Default: 4. */
	rows?: number;
}

/**
 * Righe skeleton etichetta/valore separate da un filetto (es. `PortfolioRow`,
 * `BalanceLine`): corpo di card-riepilogo dove ogni riga è "nome a sinistra,
 * numero a destra", senza avatar né grafico.
 */
const KeyValueListSkeleton = ({ rows = 4 }: KeyValueListSkeletonProps) => (
	<div className="w-full space-y-2">
		{Array.from({ length: rows }).map((_, i) => (
			<div key={i} className="flex items-center justify-between border-b border-border py-1">
				<BodySkeleton className="h-3 w-24" />
				<BodySkeleton className="h-3 w-16" />
			</div>
		))}
	</div>
);
KeyValueListSkeleton.displayName = 'KeyValueListSkeleton';

export interface RingSkeletonProps {
	/** Diametro del cerchio placeholder, in px. Default: 168 (misura di `RingGauge` in dashboard). */
	size?: number;
	/** Numero di righe etichetta/valore sotto il cerchio. Default: 3. */
	lines?: number;
}

/** Corpo skeleton di un `RingGauge` con bilancio sotto: cerchio + righe etichetta/valore. */
const RingSkeleton = ({ size = 168, lines = 3 }: RingSkeletonProps) => (
	<div className="flex flex-col items-center gap-4">
		<BodySkeleton className="shrink-0 rounded-full" style={{ width: size, height: size }} />
		<KeyValueListSkeleton rows={lines} />
	</div>
);
RingSkeleton.displayName = 'RingSkeleton';

export interface BarsSkeletonProps {
	/** Numero di righe etichetta + barra piena. Default: 3. */
	rows?: number;
}

/**
 * Righe skeleton "etichetta sopra, barra piena sotto" (es. `ProgressItem`,
 * `TimeBandBar`): corpo di card-ripartizione a barre orizzontali, come il
 * consumo per fascia oraria.
 */
const BarsSkeleton = ({ rows = 3 }: BarsSkeletonProps) => (
	<div className="space-y-3">
		{Array.from({ length: rows }).map((_, i) => (
			<div key={i} className="space-y-1.5">
				<div className="flex items-center justify-between">
					<BodySkeleton className="h-3 w-32" />
					<BodySkeleton className="h-3 w-10" />
				</div>
				<BodySkeleton className="h-2 w-full rounded-[3px]" />
			</div>
		))}
	</div>
);
BarsSkeleton.displayName = 'BarsSkeleton';

export {
	BarsSkeleton,
	BodySkeleton,
	ChartSkeleton,
	InlineControlSkeleton,
	KeyValueListSkeleton,
	KpiSkeleton,
	ListRowSkeleton,
	ListSkeleton,
	PanelCardSkeleton,
	RingSkeleton,
};
